import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useAuth0Data } from '../../../context/Auth0DataContext';
import { calculateYearTotals } from '../../../utils/calculateYearTotals';

const { FiPieChart, FiTrendingUp, FiTrendingDown, FiPercent, FiCalendar, FiInfo, FiExternalLink } = FiIcons;

const GrossMarginWidget = () => {
  const { data, dataYear, dataAvailableYears, setDataYear } = useAuth0Data();
  const { grossMargin, historicalRevenueGM } = data;

  // Generate year options
  const currentYear = new Date().getFullYear();
  const yearOptions = [...new Set([...dataAvailableYears, currentYear])].sort((a, b) => b - a);

  const handleYearChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDataYear(parseInt(e.target.value));
  };

  // Get latest data point for date reference (filtered by selected year)
  const getLatestDataPoint = () => {
    if (!historicalRevenueGM || historicalRevenueGM.length === 0) return null;
    const yearData = historicalRevenueGM.filter(p => new Date(p.date).getFullYear() === dataYear);
    if (yearData.length === 0) return null;
    const sortedData = [...yearData].sort((a, b) => new Date(b.date) - new Date(a.date));
    return sortedData[0];
  };

  const calculateYearProgress = (dataPointDate) => {
    if (!dataPointDate) return 0;
    const date = new Date(dataPointDate);
    const year = date.getFullYear();
    const startOfYear = new Date(year, 0, 1);
    const daysPassed = Math.floor((date - startOfYear) / (1000 * 60 * 60 * 24));
    const totalDaysInYear = new Date(year, 11, 31).getDate() === 31 ? 366 : 365;
    return (daysPassed / totalDaysInYear) * 100;
  };

  const latestDataPoint = getLatestDataPoint();
  const yearProgress = latestDataPoint ? calculateYearProgress(latestDataPoint.date) : 0;

  // Use selected year GM calculation
  const { totalGrossMargin: selectedYearGM } = calculateYearTotals(historicalRevenueGM, dataYear);
  const expectedGrossMargin = grossMargin.goal > 0 ? (grossMargin.goal * yearProgress) / 100 : 0;
  const percentage = grossMargin.goal > 0 ? (selectedYearGM / grossMargin.goal) * 100 : 0;
  const isOnTrack = percentage >= yearProgress;
  const performanceVsExpected = expectedGrossMargin > 0 ? ((selectedYearGM - expectedGrossMargin) / expectedGrossMargin) * 100 : 0;

  // Filter historical data by selected year for display
  const selectedYearHistoricalData = historicalRevenueGM.filter(p => new Date(p.date).getFullYear() === dataYear);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Link to="/data-management/revenue-gm" className="block">
      <motion.div
        whileHover={{ y: -8, scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-2xl border border-blue-100/50 shadow-xl shadow-blue-500/10 hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-300 cursor-pointer group"
      >
        {/* Background decoration */}
        <div className="absolute inset-0 bg-mesh-pattern opacity-30"></div>
        <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full blur-2xl"></div>
        <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-tr from-cyan-400/10 to-blue-400/10 rounded-full blur-3xl"></div>

        {/* Link indicator */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-blue-600" />
        </div>

        <div className="relative p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <motion.div
                className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25"
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
              >
                <SafeIcon icon={FiPieChart} className="w-6 h-6 text-white" />
              </motion.div>
              <div className="ml-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-gray-900">Gross Margin</h3>
                  <select
                    value={dataYear}
                    onChange={handleYearChange}
                    onClick={(e) => e.preventDefault()}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium bg-white cursor-pointer"
                  >
                    {yearOptions.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <p className="text-sm text-gray-500">
                  {latestDataPoint ? `From ${selectedYearHistoricalData.length} data points` : 'No data for this year'}
                </p>
              </div>
            </div>
            {grossMargin.goal > 0 && (
              <motion.div
                className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  isOnTrack 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-orange-100 text-orange-700'
                }`}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring" }}
              >
                <SafeIcon icon={isOnTrack ? FiTrendingUp : FiTrendingDown} className="w-4 h-4 mr-1" />
                {percentage.toFixed(1)}%
              </motion.div>
            )}
          </div>

          {/* Amount - Showing selected year GM */}
          <motion.div
            className="mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
              {formatCurrency(selectedYearGM)}
            </div>
            <p className="text-gray-600 flex items-center">
              <SafeIcon icon={FiPercent} className="w-4 h-4 mr-2" />
              Goal: {formatCurrency(grossMargin.goal)}
            </p>
            {selectedYearHistoricalData.length > 1 && (
              <p className="text-xs text-gray-500 mt-1">
                {dataYear} total from {selectedYearHistoricalData.length} data points
              </p>
            )}
          </motion.div>

          {/* Performance vs Expected */}
          {latestDataPoint && grossMargin.goal > 0 && (
            <motion.div
              className="mb-4 p-3 bg-white/60 rounded-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 flex items-center">
                  <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-1" />
                  Performance vs Expected
                </span>
                <span className={`font-semibold ${
                  performanceVsExpected >= 0 ? 'text-blue-600' : 'text-red-600'
                }`}>
                  {performanceVsExpected >= 0 ? '+' : ''}{performanceVsExpected.toFixed(1)}%
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Expected: {formatCurrency(expectedGrossMargin)} ({yearProgress.toFixed(1)}% of year)
              </div>
            </motion.div>
          )}

          {/* Progress bar */}
          {grossMargin.goal > 0 && (
            <motion.div
              className="space-y-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex justify-between text-sm text-gray-600">
                <span>Progress to Goal</span>
                <span className="font-semibold">{percentage.toFixed(1)}%</span>
              </div>
              
              <div className="relative">
                <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(percentage, 100)}%` }}
                    transition={{ duration: 1.5, ease: "easeOut", delay: 0.5 }}
                    className={`h-3 rounded-full relative overflow-hidden ${
                      isOnTrack 
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-500' 
                        : 'bg-gradient-to-r from-orange-500 to-red-500'
                    }`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                  </motion.div>
                </div>

                {/* Year progress indicator */}
                {yearProgress > 0 && (
                  <motion.div
                    className="absolute -top-1 w-2 h-5 bg-emerald-500 rounded-sm opacity-60"
                    initial={{ left: '0%' }}
                    animate={{ left: `${Math.min(yearProgress, 100)}%` }}
                    transition={{ duration: 1.5, ease: "easeOut", delay: 0.5 }}
                    style={{ transform: 'translateX(-50%)' }}
                    title={`${yearProgress.toFixed(1)}% of year elapsed`}
                  />
                )}

                {/* Progress indicator */}
                <motion.div
                  className={`absolute -top-2 w-4 h-4 rounded-full border-2 border-white shadow-lg ${
                    isOnTrack ? 'bg-blue-500' : 'bg-orange-500'
                  }`}
                  initial={{ left: '0%' }}
                  animate={{ left: `${Math.min(percentage, 100)}%` }}
                  transition={{ duration: 1.5, ease: "easeOut", delay: 0.5 }}
                  style={{ transform: 'translateX(-50%)' }}
                />
              </div>

              <div className="flex justify-between text-xs text-gray-500">
                <span>$0</span>
                <span>{formatCurrency(grossMargin.goal)}</span>
              </div>
            </motion.div>
          )}

          {grossMargin.goal === 0 && (
            <motion.div
              className="text-center py-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                <SafeIcon icon={FiPercent} className="w-4 h-4 mr-2" />
                Click to set margin goals
              </div>
            </motion.div>
          )}

          {!latestDataPoint && (
            <motion.div
              className="text-center py-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                <SafeIcon icon={FiInfo} className="w-4 h-4 mr-2" />
                Click to add GM data points
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </Link>
  );
};

export default GrossMarginWidget;